/**
 * "Add to sequence" action — enroll a contact or company in a sales cadence.
 *
 * Visibility is gated to the rollout allowlist (Miclain's emails) at the shared
 * action-filter chokepoint (`useActionFiltering`), keyed off this action's id.
 * So the action is declared for every contact/company surface but only rendered
 * for allowed users — see lib/features/sequences-access.ts.
 *
 * `allowedRoles` is a secondary, role-based belt on top of that email allowlist
 * (the allowlisted users are admins during rollout) and satisfies the detail-
 * action role-gating contract enforced by validate-action-handler-wiring.
 *
 * href-based: opens /sequences with the target prefilled in a new tab, where the
 * cadence is chosen and the enrollment created.
 */
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type { DetailAction } from "@/components/shared/UniversalDetailPage/types/action-types";

/** Action id the email gate matches on. */
export const ENROLL_IN_SEQUENCE_ACTION_ID = "enroll-in-sequence" as const;

export function createEnrollInSequenceAction<T extends BaseEntity>(opts: {
  entity: "contact" | "company";
  /** Roles allowed to see the action (role-gating contract; enforced for contacts). */
  allowedRoles: readonly string[];
}): DetailAction<T> {
  const param = opts.entity === "company" ? "enrollCompanyId" : "enrollContactId";
  return {
    id: ENROLL_IN_SEQUENCE_ACTION_ID,
    label: "Add to sequence",
    icon: "route",
    href: (item: T) => `/sequences?${param}=${item.id}`,
    openInNewTab: true,
    tooltip: "Enroll in a sales outreach cadence",
    allowedRoles: [...opts.allowedRoles],
  };
}
